import React from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowUp, ArrowDown } from "lucide-react";
import PlanFeatureGroups from "./PlanFeatureGroups";
import { getIncludedPlanFeatures } from "./planFeatureUtils";

/**
 * Confirmation modal for changing subscription tier.
 * For paid tiers, shows pricing info and triggers Stripe Checkout.
 * For free tiers (or downgrade to free), changes immediately.
 */
export default function ChangeSubscriptionModal({
  open,
  onClose,
  selectedTier,
  currentTier,
  onConfirm,
  loading,
}) {
  const reason = "";

  if (!selectedTier) return null;

  const isUpgrade = selectedTier.sort_order > (currentTier?.sort_order ?? -1);
  const isDowngrade = !isUpgrade;
  const includedFeatures = getIncludedPlanFeatures(selectedTier);

  const handleConfirm = () => {
    onConfirm?.(selectedTier, reason);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isUpgrade ? "Upgrade Subscription" : "Change Plan"}
      maxWidthClass="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={isUpgrade ? "default" : "secondary"}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading
              ? "Processing..."
              : isUpgrade
              ? "Confirm Upgrade"
              : "Confirm Change"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Direction indicator */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/10">
          {isUpgrade ? (
            <ArrowUp className="w-8 h-8 text-success" />
          ) : (
            <ArrowDown className="w-8 h-8 text-yellow-500" />
          )}
          <div>
            <p className="text-sm text-textSecondary">
              {isUpgrade ? "Upgrading to" : "Switching to"}
            </p>
            <p className="text-lg font-semibold text-textPrimary">
              {selectedTier.name}
            </p>
          </div>
          <Badge variant={isUpgrade ? "success" : "warning"} className="ml-auto">
            {isUpgrade ? "Upgrade" : "Downgrade"}
          </Badge>
        </div>

        {/* Description */}
        {selectedTier.description && (
          <p className="text-sm text-textSecondary">{selectedTier.description}</p>
        )}

        {/* Downgrade warning */}
        {isDowngrade && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-300">
              <p className="font-medium">Important</p>
              <p className="mt-1">
                Downgrading may reduce your feature limits. Your current usage
                data will be preserved, but quotas will be adjusted to match
                the new plan.
              </p>
            </div>
          </div>
        )}

        {/* Features */}
        <div>
          <h4 className="text-sm font-medium text-textPrimary mb-2">
            Plan includes:
          </h4>
          <PlanFeatureGroups features={includedFeatures} />
        </div>
      </div>
    </Modal>
  );
}
